import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import { USER_ROLES } from '../config/security'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './PainelProcessosClientes.css'

const EMPRESA_HANGSIX = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

async function fetchAllPages(makeQuery, pageSize = 1000) {
  const all = []
  let from = 0
  for (;;) {
    const { data, error } = await makeQuery().range(from, from + pageSize - 1)
    if (error) throw error
    const chunk = data || []
    all.push(...chunk)
    if (chunk.length < pageSize) break
    from += pageSize
    if (from > 30000) break
  }
  return all
}

function etapaPorTipo(etapas, tipo) {
  return (etapas || []).find((e) => e.tipo_etapa === tipo) || null
}

function etapaAprovada(etapa) {
  return etapa?.status === 'aprovado'
}

function etapaIniciada(etapa) {
  if (!etapa) return false
  return etapa.status === 'aprovado' || !!(etapa.protocolo && String(etapa.protocolo).trim()) || !!etapa.data_etapa
}

function calcularDiasCorridos(dataInicio) {
  if (!dataInicio) return null
  const inicio = new Date(`${String(dataInicio).slice(0, 10)}T00:00:00`)
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  if (Number.isNaN(inicio.getTime())) return null
  return Math.floor((hoje - inicio) / (1000 * 60 * 60 * 24))
}

function calcularDiasUteis(dataInicio) {
  if (!dataInicio) return null
  const atual = new Date(`${String(dataInicio).slice(0, 10)}T00:00:00`)
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  if (Number.isNaN(atual.getTime())) return null
  let diasUteis = 0
  while (atual < hoje) {
    atual.setDate(atual.getDate() + 1)
    const diaSemana = atual.getDay()
    if (diaSemana !== 0 && diaSemana !== 6) diasUteis++
  }
  return diasUteis
}

function formatarData(value) {
  if (!value) return ''
  const raw = String(value)
  const iso = raw.includes('T') ? raw : `${raw.slice(0, 10)}T00:00:00`
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('pt-BR')
}

function chaveCliente(id) {
  return id ? `id:${id}` : null
}

function chaveFluxoSemCliente(fluxoId) {
  return `fluxo:${fluxoId}`
}

function pickMelhorOs(lista) {
  if (!lista?.length) return null
  const rank = (status) => {
    if (status === 'Validado' || status === 'Finalizado') return 5
    if (status === 'Em andamento') return 4
    if (status === 'Novo') return 3
    if (status === 'Cancelado') return 1
    return 2
  }
  return [...lista].sort((a, b) => {
    const d = rank(b.status) - rank(a.status)
    if (d) return d
    return String(b.created_at || '').localeCompare(String(a.created_at || ''))
  })[0]
}

function resumoInstalacao(os) {
  if (!os) {
    return { texto: 'Não iniciado', tom: 'muted', estado: 'nao_iniciado', atrasado: false }
  }
  const status = os.status || 'Novo'
  if (status === 'Finalizado' || status === 'Validado') {
    return { texto: 'Instalação concluída', tom: 'success', estado: 'concluido', atrasado: false }
  }
  if (status === 'Em andamento') {
    return { texto: 'Instalação em andamento', tom: 'warning', estado: 'andamento', atrasado: false }
  }
  if (status === 'Cancelado') {
    return { texto: 'Cancelada', tom: 'danger', estado: 'cancelado', atrasado: false }
  }
  return { texto: 'Aguardando início', tom: 'muted', estado: 'inicio', atrasado: false }
}

function vistoriaCampoFeita(status) {
  const s = String(status || '').toLowerCase()
  return ['aprovado', 'aprovado com obra', 'concluida', 'finalizado', 'validado', 'revisado'].includes(s)
}

function resumoVistoria(os, form) {
  const status = form?.status || os?.status
  if (!os && !form) {
    return { texto: 'Não iniciada', tom: 'muted', estado: 'nao_iniciado', atrasado: false }
  }
  if (vistoriaCampoFeita(status)) {
    return { texto: 'Vistoria realizada', tom: 'success', estado: 'concluido', atrasado: false }
  }
  const s = String(status || '')
  if (s === 'Reprovado' || s === 'Reprovada') {
    return { texto: 'Reprovada', tom: 'danger', estado: 'reprovado', atrasado: false }
  }
  if (s === 'Cancelado' || s === 'Cancelada') {
    return { texto: 'Cancelada', tom: 'danger', estado: 'cancelado', atrasado: false }
  }
  if (s === 'Em andamento') {
    return { texto: 'Em andamento', tom: 'warning', estado: 'andamento', atrasado: false }
  }
  if (s === 'Novo' || s === 'Pendente') {
    return { texto: 'Pendente', tom: 'muted', estado: 'inicio', atrasado: false }
  }
  return { texto: s || 'Em andamento', tom: 'warning', estado: 'andamento', atrasado: false }
}

function resumoHomologacao(fluxo) {
  if (!fluxo) {
    return { texto: 'Não iniciada', tom: 'muted', estado: 'nao_iniciado', atrasado: false }
  }
  if (fluxo.homologado) {
    return { texto: 'Homologado', tom: 'success', estado: 'concluido', atrasado: false }
  }
  const parecer = etapaPorTipo(fluxo.etapas, 'parecer')
  const vistoria = etapaPorTipo(fluxo.etapas, 'vistoria')
  const carga = etapaPorTipo(fluxo.etapas, 'aumento_carga')

  const parecerAtrasado = !etapaAprovada(parecer) && calcularDiasCorridos(parecer?.data_etapa) > 15
  const vistoriaAtrasada = !etapaAprovada(vistoria) && calcularDiasUteis(vistoria?.data_etapa) > 5
  const cargaAtrasada = !etapaAprovada(carga) && calcularDiasUteis(carga?.data_etapa) > 5
  const atrasado = !!(parecerAtrasado || vistoriaAtrasada || cargaAtrasada)

  if (etapaAprovada(parecer) && etapaAprovada(vistoria) && etapaAprovada(carga)) {
    return { texto: 'Aguardando homologação', tom: 'warning', estado: 'andamento', atrasado }
  }
  if (etapaIniciada(carga) && !etapaAprovada(carga)) {
    return { texto: atrasado ? 'Aumento de Carga (atrasado)' : 'Aumento de Carga (em análise)', tom: atrasado ? 'danger' : 'warning', estado: 'andamento', atrasado }
  }
  if (etapaIniciada(vistoria) && !etapaAprovada(vistoria)) {
    return { texto: atrasado ? 'Vistoria de conexão (atrasada)' : 'Vistoria de conexão (em análise)', tom: atrasado ? 'danger' : 'warning', estado: 'andamento', atrasado }
  }
  if (etapaAprovada(parecer)) {
    return { texto: 'Parecer de Acesso emitido', tom: 'warning', estado: 'andamento', atrasado }
  }
  if (etapaIniciada(parecer)) {
    return { texto: atrasado ? 'Parecer de Acesso (atrasado)' : 'Solicitação de parecer', tom: atrasado ? 'danger' : 'info', estado: 'inicio', atrasado }
  }
  return { texto: 'Não iniciada', tom: 'muted', estado: 'nao_iniciado', atrasado: false }
}

function statusGeral(inst, vist, hom) {
  if (hom.atrasado) return { texto: 'Atrasado', classe: 'bg-danger' }
  if (inst.estado === 'concluido' && vist.estado === 'concluido' && hom.estado === 'concluido') {
    return { texto: 'Concluído', classe: 'bg-success' }
  }
  if ([inst, vist, hom].some((x) => x.estado === 'reprovado' || x.estado === 'cancelado')) {
    return { texto: 'Atenção', classe: 'bg-danger' }
  }
  const iniciados = [inst, vist, hom].filter((x) => x.estado !== 'nao_iniciado').length
  if (iniciados === 0) return { texto: 'Início', classe: 'bg-info text-dark' }
  return { texto: 'Em Andamento', classe: 'bg-warning text-dark' }
}

function classeTom(tom) {
  if (tom === 'success') return 'text-success'
  if (tom === 'warning') return 'text-warning'
  if (tom === 'danger') return 'text-danger'
  if (tom === 'info') return 'text-info'
  return 'text-muted'
}

function iconeTimeline(estado) {
  if (estado === 'done') return { cls: 'bg-success text-white', icon: 'bi-check' }
  if (estado === 'current') return { cls: 'bg-warning text-dark', icon: 'bi-clock-fill' }
  if (estado === 'danger') return { cls: 'bg-danger text-white', icon: 'bi-exclamation' }
  return { cls: 'bg-secondary text-white', icon: 'bi-dash' }
}

function passoEtapaFluxo(etapa, { atrasadoLimite, tipoDias }) {
  if (etapaAprovada(etapa)) return { estado: 'done', detalhe: etapa.data_etapa ? `Aprovado em ${formatarData(etapa.data_etapa)}` : 'Aprovado' }
  if (etapaIniciada(etapa)) {
    const dias = tipoDias === 'uteis' ? calcularDiasUteis(etapa.data_etapa) : calcularDiasCorridos(etapa.data_etapa)
    const atrasado = dias != null && dias > atrasadoLimite
    return {
      estado: atrasado ? 'danger' : 'current',
      detalhe: etapa.data_etapa
        ? `${atrasado ? 'Atrasado' : 'Em análise'} · ${formatarData(etapa.data_etapa)}`
        : (etapa.protocolo ? `Protocolo ${etapa.protocolo}` : 'Em análise')
    }
  }
  return { estado: 'pending', detalhe: 'Não iniciado' }
}

function passoOs(os, formStatus) {
  const status = formStatus || os?.status
  if (!os && !formStatus) return { criado: 'pending', andamento: 'pending', fim: 'pending', detalhe: 'Não iniciado' }
  if (vistoriaCampoFeita(status) || status === 'Finalizado' || status === 'Validado') {
    return { criado: 'done', andamento: 'done', fim: 'done', detalhe: status || 'Concluído' }
  }
  if (status === 'Reprovado' || status === 'Cancelado' || status === 'Cancelada') {
    return { criado: 'done', andamento: 'danger', fim: 'pending', detalhe: status }
  }
  if (status === 'Em andamento') {
    return { criado: 'done', andamento: 'current', fim: 'pending', detalhe: 'Em andamento' }
  }
  return { criado: 'current', andamento: 'pending', fim: 'pending', detalhe: status || 'Aguardando início' }
}

function TimelineItem({ titulo, detalhe, estado }) {
  const { cls, icon } = iconeTimeline(estado)
  return (
    <li className="timeline-modal-item">
      <span className={`timeline-modal-icon ${cls}`}><i className={`bi ${icon}`} /></span>
      <div className="ms-2">
        <span className={`fw-semibold ${estado === 'pending' ? 'text-muted' : 'text-dark'}`}>{titulo}</span>
        <div className="text-muted small">{detalhe}</div>
      </div>
    </li>
  )
}

function LinhaInfo({ label, valor }) {
  const texto = valor === 0 ? '0' : (valor || '—')
  return (
    <div className="ppc-info-linha">
      <span className="text-muted">{label}</span>
      <span className="fw-semibold text-dark text-end">{texto}</span>
    </div>
  )
}

function statusEtapaLabel(status) {
  const s = String(status || 'pendente').toLowerCase()
  if (s === 'aprovado') return 'Aprovado'
  if (s === 'reprovado') return 'Reprovado'
  if (s === 'atrasado') return 'Atrasado'
  if (s === 'aguardando') return 'Aguardando'
  return 'Pendente'
}

function BlocoProcesso({ titulo, icone, cor, resumo, children, onAbrir }) {
  return (
    <button type="button" className="ppc-processo-bloco h-100" onClick={onAbrir}>
      <div className="ppc-processo-topo mb-3">
        <h6 className={`fw-bold ${cor} mb-0`}><i className={`bi ${icone}`} /> {titulo}</h6>
        <span className="ppc-ver-info">Ver informações <i className="bi bi-chevron-right" /></span>
      </div>
      {resumo ? <p className={`small mb-3 ppc-processo-resumo ${classeTom(resumo.tom)}`}>{resumo.texto}</p> : null}
      {children}
    </button>
  )
}

export default function PainelProcessosClientes({ onBack, userRole, userEmpresa }) {
  const empresaId = userEmpresa?.id || userEmpresa || null
  const isAdmin = userRole === USER_ROLES.ADMINISTRADOR
  const veTodas = isAdmin || String(empresaId || '') === EMPRESA_HANGSIX

  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const [itens, setItens] = useState([])
  const [empresas, setEmpresas] = useState([])
  const [filtroBusca, setFiltroBusca] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroEmpresa, setFiltroEmpresa] = useState('')
  const [selecionado, setSelecionado] = useState(null)
  const [processoAberto, setProcessoAberto] = useState(null)
  const [tiposPorId, setTiposPorId] = useState({})

  const nomeEmpresaPorId = useCallback((id) => {
    if (!id) return '–'
    if (String(id) === String(empresaId)) return userEmpresa?.empresa || '–'
    const emp = empresas.find((e) => String(e.id) === String(id))
    return emp?.empresa || '–'
  }, [empresaId, empresas, userEmpresa])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setErro('')

      const [{ data: tipos, error: errTipos }, empresasRes] = await Promise.all([
        supabase.from('tipo_servico').select('id, nome'),
        veTodas
          ? supabase.from('empresa').select('id, empresa').order('empresa')
          : Promise.resolve({ data: [] })
      ])
      if (errTipos) throw errTipos
      setEmpresas(empresasRes.data || [])
      const mapaTipos = {}
      for (const t of tipos || []) mapaTipos[String(t.id)] = t.nome
      setTiposPorId(mapaTipos)

      const tiposLista = tipos || []
      const idVistoria = tiposLista.find((t) => String(t.nome || '').trim().toLowerCase() === 'vistoria')?.id || null
      const idsInstalacao = new Set(
        tiposLista
          .filter((t) => String(t.nome || '').trim().toLowerCase() !== 'vistoria')
          .map((t) => String(t.id))
      )

      const qFluxo = () => {
        let q = supabase
          .from('fluxo_cliente')
          .select('id, empresa_id, cliente_id, nome, uc, homologado, data_homologado, created_at, etapas:fluxo_cliente_etapas(*), cliente:clientes_cadastro(id, nome_completo, homologado, data_homologado)')
          .order('created_at', { ascending: false })
        if (!veTodas && empresaId) q = q.eq('empresa_id', empresaId)
        if (veTodas && filtroEmpresa) q = q.eq('empresa_id', filtroEmpresa)
        return q
      }

      const qOs = () => {
        let q = supabase
          .from('ordens_servicos')
          .select('id, id_cliente, id_tipo_servico, id_empresa, status, data_execucao, data_criacao, created_at, obs_operacional, cliente:clientes_cadastro(id, nome_completo, empresa_id)')
          .order('created_at', { ascending: false })
        if (!veTodas && empresaId) q = q.eq('id_empresa', empresaId)
        if (veTodas && filtroEmpresa) q = q.eq('id_empresa', filtroEmpresa)
        return q
      }

      const qVistorias = () => {
        let q = supabase
          .from('vistorias')
          .select('id, id_cliente, id_empresa, status, data_vistoria, created_at, ligacao_padrao, local_inversor, conexao_inversor, posicao_poste_concessionaria, observacoes')
          .order('created_at', { ascending: false })
        if (!veTodas && empresaId) q = q.eq('id_empresa', empresaId)
        if (veTodas && filtroEmpresa) q = q.eq('id_empresa', filtroEmpresa)
        return q
      }

      const [fluxos, ordens, formsVistoria] = await Promise.all([
        fetchAllPages(qFluxo),
        fetchAllPages(qOs),
        fetchAllPages(qVistorias)
      ])

      const formsPorCliente = new Map()
      for (const v of formsVistoria || []) {
        if (!v.id_cliente) continue
        if (!veTodas && empresaId && String(v.id_empresa) !== String(empresaId)) continue
        if (veTodas && filtroEmpresa && String(v.id_empresa) !== String(filtroEmpresa)) continue
        const atual = formsPorCliente.get(v.id_cliente)
        if (!atual || String(v.created_at || '') > String(atual.created_at || '')) {
          formsPorCliente.set(v.id_cliente, v)
        }
      }

      const mapa = new Map()

      const garantir = (key, base) => {
        if (!mapa.has(key)) {
          mapa.set(key, {
            key,
            clienteId: base.clienteId || null,
            nome: base.nome || 'Cliente',
            uc: base.uc || '',
            empresaId: base.empresaId || null,
            codigo: base.codigo || '',
            fluxo: null,
            osInstalacao: [],
            osVistoria: [],
            formVistoria: null
          })
        }
        const row = mapa.get(key)
        if (base.nome && (!row.nome || row.nome === 'Cliente')) row.nome = base.nome
        if (base.uc && !row.uc) row.uc = base.uc
        if (base.empresaId && !row.empresaId) row.empresaId = base.empresaId
        if (base.clienteId && !row.clienteId) row.clienteId = base.clienteId
        return row
      }

      for (const f of fluxos || []) {
        const key = f.cliente_id ? chaveCliente(f.cliente_id) : chaveFluxoSemCliente(f.id)
        const row = garantir(key, {
          clienteId: f.cliente_id,
          nome: f.nome,
          uc: f.uc,
          empresaId: f.empresa_id,
          codigo: f.uc ? `UC ${f.uc}` : (f.cliente_id ? `#${String(f.cliente_id).slice(0, 8)}` : `#${String(f.id).slice(0, 8)}`)
        })
        if (!row.fluxo || String(f.created_at || '') > String(row.fluxo.created_at || '')) {
          row.fluxo = {
            ...f,
            homologado: f.homologado === true || f.cliente?.homologado === true,
            data_homologado: f.data_homologado || f.cliente?.data_homologado || null
          }
          if (f.uc) row.uc = f.uc
          if (f.cliente?.nome_completo) row.nome = f.cliente.nome_completo
        }
      }

      for (const os of ordens || []) {
        const clienteId = os.id_cliente || os.cliente?.id
        if (!clienteId) continue
        const key = chaveCliente(clienteId)
        const row = garantir(key, {
          clienteId,
          nome: os.cliente?.nome_completo,
          empresaId: os.id_empresa || os.cliente?.empresa_id,
          codigo: `#${String(clienteId).slice(0, 8)}`
        })
        if (idVistoria && String(os.id_tipo_servico) === String(idVistoria)) {
          row.osVistoria.push(os)
        } else if (!idVistoria || idsInstalacao.has(String(os.id_tipo_servico))) {
          row.osInstalacao.push(os)
        }
      }

      for (const [clienteId, form] of formsPorCliente.entries()) {
        const key = chaveCliente(clienteId)
        const row = garantir(key, {
          clienteId,
          empresaId: form.id_empresa,
          codigo: `#${String(clienteId).slice(0, 8)}`
        })
        row.formVistoria = form
      }

      const lista = [...mapa.values()].map((row) => {
        const osInst = pickMelhorOs(row.osInstalacao)
        const osVist = pickMelhorOs(row.osVistoria)
        const inst = resumoInstalacao(osInst)
        const vist = resumoVistoria(osVist, row.formVistoria)
        const hom = resumoHomologacao(row.fluxo)
        const geral = statusGeral(inst, vist, hom)
        return {
          ...row,
          osInst,
          osVist,
          inst,
          vist,
          hom,
          geral,
          codigo: row.uc ? `UC ${row.uc}` : (row.codigo || '—')
        }
      }).sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR'))

      setItens(lista)
    } catch (e) {
      console.error(e)
      setErro('Erro ao carregar processos: ' + e.message)
      setItens([])
    } finally {
      setLoading(false)
    }
  }, [empresaId, veTodas, filtroEmpresa])

  useEffect(() => {
    load()
  }, [load])

  const filtrados = useMemo(() => {
    const busca = filtroBusca.trim().toLowerCase()
    return itens.filter((item) => {
      if (busca) {
        const nome = (item.nome || '').toLowerCase()
        const uc = (item.uc || '').toLowerCase()
        const codigo = (item.codigo || '').toLowerCase()
        if (!nome.includes(busca) && !uc.includes(busca) && !codigo.includes(busca)) return false
      }
      if (filtroStatus && item.geral.texto !== filtroStatus) return false
      return true
    })
  }, [itens, filtroBusca, filtroStatus])

  const fecharModal = () => {
    setSelecionado(null)
    setProcessoAberto(null)
  }

  const tituloProcesso = {
    instalacao: 'Instalação',
    vistoria: 'Vistoria de campo',
    homologacao: 'Homologação'
  }

  const renderDetalheProcesso = () => {
    if (!selecionado || !processoAberto) return null

    if (processoAberto === 'instalacao') {
      const ordens = selecionado.osInstalacao || []
      if (!ordens.length) {
        return <p className="text-muted mb-0">Este processo ainda não foi iniciado na Gestão de Instalação.</p>
      }
      return (
        <div className="d-flex flex-column gap-3">
          {ordens.map((os) => (
            <div key={os.id} className="ppc-info-card">
              <div className="fw-semibold mb-2">{tiposPorId[String(os.id_tipo_servico)] || 'Ordem de serviço'}</div>
              <LinhaInfo label="Status" valor={os.status} />
              <LinhaInfo label="Data de criação" valor={formatarData(os.data_criacao || os.created_at)} />
              <LinhaInfo label="Data de execução" valor={formatarData(os.data_execucao)} />
              <LinhaInfo label="Observação operacional" valor={os.obs_operacional} />
            </div>
          ))}
        </div>
      )
    }

    if (processoAberto === 'vistoria') {
      const os = selecionado.osVist
      const form = selecionado.formVistoria
      if (!os && !form) {
        return <p className="text-muted mb-0">Este processo ainda não foi iniciado em Vistorias.</p>
      }
      return (
        <div className="d-flex flex-column gap-3">
          {os ? (
            <div className="ppc-info-card">
              <div className="fw-semibold mb-2">Ordem de vistoria</div>
              <LinhaInfo label="Status da OS" valor={os.status} />
              <LinhaInfo label="Data de criação" valor={formatarData(os.data_criacao || os.created_at)} />
              <LinhaInfo label="Data de execução" valor={formatarData(os.data_execucao)} />
            </div>
          ) : null}
          {form ? (
            <div className="ppc-info-card">
              <div className="fw-semibold mb-2">Formulário de vistoria</div>
              <LinhaInfo label="Parecer / status" valor={form.status} />
              <LinhaInfo label="Data da vistoria" valor={formatarData(form.data_vistoria)} />
              <LinhaInfo label="Ligação" valor={form.ligacao_padrao} />
              <LinhaInfo label="Local do inversor" valor={form.local_inversor} />
              <LinhaInfo label="Conexão do inversor" valor={form.conexao_inversor} />
              <LinhaInfo label="Poste da concessionária" valor={form.posicao_poste_concessionaria} />
              <LinhaInfo label="Observações" valor={form.observacoes} />
            </div>
          ) : (
            <p className="text-muted mb-0">A ordem existe, mas o formulário de baixa ainda não foi preenchido.</p>
          )}
        </div>
      )
    }

    const f = selecionado.fluxo
    if (!f) {
      return <p className="text-muted mb-0">Este processo ainda não foi iniciado no Fluxo do Cliente.</p>
    }
    const etapasInfo = [
      { tipo: 'parecer', nome: 'Parecer de Acesso' },
      { tipo: 'aumento_carga', nome: 'Aumento de Carga' },
      { tipo: 'vistoria', nome: 'Vistoria de conexão' }
    ]
    return (
      <div className="d-flex flex-column gap-3">
        <div className="ppc-info-card">
          <div className="fw-semibold mb-2">Processo</div>
          <LinhaInfo label="UC" valor={f.uc || selecionado.uc} />
          <LinhaInfo label="Cliente homologado" valor={f.homologado ? 'Sim' : 'Não'} />
          <LinhaInfo label="Data da homologação" valor={formatarData(f.data_homologado)} />
        </div>
        {etapasInfo.map((et) => {
          const etapa = etapaPorTipo(f.etapas, et.tipo)
          return (
            <div key={et.tipo} className="ppc-info-card">
              <div className="fw-semibold mb-2">{et.nome}</div>
              <LinhaInfo label="Status" valor={statusEtapaLabel(etapa?.status)} />
              <LinhaInfo label="Protocolo" valor={etapa?.protocolo} />
              <LinhaInfo label="Data" valor={formatarData(etapa?.data_etapa)} />
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="ppc-app">
      <div className="container py-3 py-md-4">
        <button type="button" className="ppc-link-voltar d-block" onClick={onBack}>← Voltar</button>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom">
          <div>
            <h1 className="h4 fw-bold text-dark mb-1">Painel de Processos dos Clientes</h1>
            <p className="text-muted small mb-0">Clique em qualquer card para ver a timeline de homologação, instalação e vistoria.</p>
          </div>
          <div className="mt-3 mt-md-0">
            <span className="badge bg-primary px-3 py-2">Total: {filtrados.length} cliente{filtrados.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {veTodas && (
            <div className="col-12 col-md-3">
              <label className="form-label mb-1" htmlFor="ppc-empresa">Empresa</label>
              <select id="ppc-empresa" className="form-select" value={filtroEmpresa} onChange={(e) => setFiltroEmpresa(e.target.value)}>
                <option value="">Todas as empresas</option>
                {empresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.empresa}</option>
                ))}
              </select>
            </div>
          )}
          <div className={veTodas ? 'col-12 col-md-5' : 'col-12 col-md-7'}>
            <label className="form-label mb-1" htmlFor="ppc-busca">Cliente ou UC</label>
            <input
              id="ppc-busca"
              className="form-control"
              placeholder="Buscar por nome ou UC"
              value={filtroBusca}
              onChange={(e) => setFiltroBusca(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label mb-1" htmlFor="ppc-status">Situação</label>
            <select id="ppc-status" className="form-select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
              <option value="">Todas</option>
              <option value="Início">Início</option>
              <option value="Em Andamento">Em Andamento</option>
              <option value="Atrasado">Atrasado</option>
              <option value="Atenção">Atenção</option>
              <option value="Concluído">Concluído</option>
            </select>
          </div>
        </div>

        {erro && <div className="alert alert-danger py-2">{erro}</div>}
        {loading && <p className="text-muted">Carregando processos...</p>}
        {!loading && filtrados.length === 0 && (
          <p className="text-muted mb-0">Nenhum cliente encontrado com processo de homologação, instalação ou vistoria.</p>
        )}

        <div className="row g-3">
          {filtrados.map((item) => (
            <div key={item.key} className="col-12 col-md-6 col-xl-4">
              <button type="button" className="card card-custom p-3 bg-white h-100" onClick={() => { setProcessoAberto(null); setSelecionado(item) }}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <span className="text-muted small">{item.codigo}</span>
                    <h6 className="fw-bold mb-0 text-dark">{item.nome}</h6>
                    {veTodas && (
                      <span className="text-muted small">{nomeEmpresaPorId(item.empresaId)}</span>
                    )}
                  </div>
                  <span className={`badge ${item.geral.classe} badge-status`}>{item.geral.texto}</span>
                </div>
                <hr className="text-muted opacity-25 my-2" />
                <div className="small mb-2 ppc-linha-status">
                  <span className="fw-semibold text-secondary"><i className="bi bi-tools" /> Instalação:</span>
                  <span className={classeTom(item.inst.tom)}>{item.inst.texto}</span>
                </div>
                <div className="small mb-2 ppc-linha-status">
                  <span className="fw-semibold text-secondary"><i className="bi bi-clipboard-check" /> Vistoria:</span>
                  <span className={classeTom(item.vist.tom)}>{item.vist.texto}</span>
                </div>
                <div className="small ppc-linha-status">
                  <span className="fw-semibold text-secondary"><i className="bi bi-file-earmark-check" /> Homologação:</span>
                  <span className={classeTom(item.hom.tom)}>{item.hom.texto}</span>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {selecionado && (
        <div className="ppc-modal-overlay" onClick={fecharModal}>
          <div className="ppc-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content ppc-modal-content">
              <div className="modal-header ppc-modal-header">
                <div className="ppc-modal-header-inner">
                  <span className="ppc-modal-code small">{selecionado.codigo}</span>
                  <h5 className="modal-title fw-bold mb-0">{selecionado.nome}</h5>
                  {veTodas && <div className="ppc-modal-code small">{nomeEmpresaPorId(selecionado.empresaId)}</div>}
                </div>
                <button type="button" className="btn-close btn-close-white" aria-label="Fechar" onClick={fecharModal} />
              </div>
              <div className="modal-body p-4">
                {processoAberto ? (
                  <>
                    <button type="button" className="ppc-link-voltar d-block mb-3" onClick={() => setProcessoAberto(null)}>
                      ← Voltar à timeline
                    </button>
                    <h6 className="fw-bold mb-3">{tituloProcesso[processoAberto]}</h6>
                    {renderDetalheProcesso()}
                  </>
                ) : (
                  <div className="row g-3">
                    <div className="col-12 col-lg-4">
                      <BlocoProcesso
                        titulo="Instalação"
                        icone="bi-tools"
                        cor="text-primary"
                        resumo={selecionado.inst}
                        onAbrir={() => setProcessoAberto('instalacao')}
                      >
                        <ul className="timeline-modal">
                          {(() => {
                            const passo = passoOs(selecionado.osInst)
                            return (
                              <>
                                <TimelineItem titulo="1. Ordem de serviço" detalhe={passo.criado === 'pending' ? 'Não iniciada' : 'OS criada'} estado={passo.criado} />
                                <TimelineItem titulo="2. Execução" detalhe={passo.andamento === 'pending' ? 'Aguardando início' : selecionado.inst.texto} estado={passo.andamento} />
                                <TimelineItem titulo="3. Baixa / conclusão" detalhe={passo.fim === 'done' ? (selecionado.osInst?.data_execucao ? formatarData(selecionado.osInst.data_execucao) : 'Concluída') : 'Pendente'} estado={passo.fim} />
                              </>
                            )
                          })()}
                        </ul>
                      </BlocoProcesso>
                    </div>
                    <div className="col-12 col-lg-4">
                      <BlocoProcesso
                        titulo="Vistoria de campo"
                        icone="bi-clipboard-check"
                        cor="text-success"
                        resumo={selecionado.vist}
                        onAbrir={() => setProcessoAberto('vistoria')}
                      >
                        <ul className="timeline-modal">
                          {(() => {
                            const passo = passoOs(selecionado.osVist, selecionado.formVistoria?.status)
                            const dataVist = selecionado.formVistoria?.data_vistoria || selecionado.osVist?.data_execucao
                            return (
                              <>
                                <TimelineItem titulo="1. Ordem de vistoria" detalhe={passo.criado === 'pending' ? 'Não iniciada' : 'OS criada'} estado={passo.criado} />
                                <TimelineItem titulo="2. Em campo" detalhe={passo.andamento === 'pending' ? 'Aguardando' : selecionado.vist.texto} estado={passo.andamento} />
                                <TimelineItem titulo="3. Parecer da vistoria" detalhe={passo.fim === 'done' ? (dataVist ? formatarData(dataVist) : selecionado.vist.texto) : 'Pendente'} estado={passo.fim} />
                              </>
                            )
                          })()}
                        </ul>
                      </BlocoProcesso>
                    </div>
                    <div className="col-12 col-lg-4">
                      <BlocoProcesso
                        titulo="Homologação"
                        icone="bi-file-earmark-check"
                        cor="text-info"
                        resumo={selecionado.hom}
                        onAbrir={() => setProcessoAberto('homologacao')}
                      >
                        <ul className="timeline-modal">
                          {(() => {
                            const f = selecionado.fluxo
                            const parecer = passoEtapaFluxo(etapaPorTipo(f?.etapas, 'parecer'), { atrasadoLimite: 15, tipoDias: 'corridos' })
                            const carga = passoEtapaFluxo(etapaPorTipo(f?.etapas, 'aumento_carga'), { atrasadoLimite: 5, tipoDias: 'uteis' })
                            const vistoriaConc = passoEtapaFluxo(etapaPorTipo(f?.etapas, 'vistoria'), { atrasadoLimite: 5, tipoDias: 'uteis' })
                            const homolog = f?.homologado
                              ? { estado: 'done', detalhe: f.data_homologado ? formatarData(f.data_homologado) : 'Cliente homologado' }
                              : { estado: 'pending', detalhe: 'Não homologado' }
                            return (
                              <>
                                <TimelineItem titulo="Parecer de Acesso" detalhe={f ? parecer.detalhe : 'Não iniciada'} estado={f ? parecer.estado : 'pending'} />
                                <TimelineItem titulo="Aumento de Carga" detalhe={f ? carga.detalhe : 'Não iniciado'} estado={f ? carga.estado : 'pending'} />
                                <TimelineItem titulo="Vistoria de conexão" detalhe={f ? vistoriaConc.detalhe : 'Não iniciada'} estado={f ? vistoriaConc.estado : 'pending'} />
                                <TimelineItem titulo="Cliente homologado" detalhe={homolog.detalhe} estado={homolog.estado} />
                              </>
                            )
                          })()}
                        </ul>
                      </BlocoProcesso>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer ppc-modal-footer">
                {processoAberto ? (
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setProcessoAberto(null)}>Voltar</button>
                ) : null}
                <button type="button" className="btn btn-secondary btn-sm" onClick={fecharModal}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
